/**
 * Keyboard + mouse input capture and bitfield generation.
 * Mouse-aim control scheme: ship auto-rotates toward cursor,
 * WASD = forward/reverse/strafe, mouse = aim direction.
 */

import { encodeInput, encodeInputMessage } from '@warmancer/shared';
import type { InputFrame } from '@warmancer/shared';
import { useGameStore } from '../../stores/gameStore';

const CONTEXT_MENU_DETECT_DELAY_MS = 50;

interface KeyState {
  thrust: boolean;
  strafeLeft: boolean;
  strafeRight: boolean;
  brake: boolean;
  afterburner: boolean;
  fire: boolean;
}

export class InputManager {
  private keys: KeyState = {
    thrust: false,
    strafeLeft: false,
    strafeRight: false,
    brake: false,
    afterburner: false,
    fire: false,
  };

  private ws: WebSocket | null = null;
  private boundKeyDown: (e: KeyboardEvent) => void;
  private boundKeyUp: (e: KeyboardEvent) => void;
  private boundBlur: () => void;
  private boundVisibilityChange: () => void;
  private boundContextMenu: (e: Event) => void;
  private boundMouseUp: (e: MouseEvent) => void;
  private boundMouseMove: (e: MouseEvent) => void;
  private boundMouseDown: (e: MouseEvent) => void;
  private contextMenuPendingTimer: ReturnType<typeof setTimeout> | null = null;
  private contextMenuFired = false;

  // Suppression: when true, tick() returns null and key events are ignored.
  // Used when settings panel or other UI overlays are open.
  private suppressed = false;

  // Mouse-aim state
  private targetAngle = 0;
  private mouseScreenX = 0;
  private mouseScreenY = 0;
  private shipWorldX = 0;
  private shipWorldY = 0;
  private mouseHasMoved = false;
  private unprojectFn: ((sx: number, sy: number) => { x: number; y: number }) | null = null;

  constructor() {
    this.boundKeyDown = this.onKeyDown.bind(this);
    this.boundKeyUp = this.onKeyUp.bind(this);
    this.boundBlur = this.onBlur.bind(this);
    this.boundVisibilityChange = this.onVisibilityChange.bind(this);
    this.boundContextMenu = this.onContextMenu.bind(this);
    this.boundMouseUp = this.onMouseUp.bind(this);
    this.boundMouseMove = this.onMouseMove.bind(this);
    this.boundMouseDown = this.onMouseDown.bind(this);
  }

  start(ws: WebSocket): void {
    this.ws = ws;
    window.addEventListener('keydown', this.boundKeyDown);
    window.addEventListener('keyup', this.boundKeyUp);
    window.addEventListener('blur', this.boundBlur);
    document.addEventListener('visibilitychange', this.boundVisibilityChange);
    document.addEventListener('contextmenu', this.boundContextMenu, true);
    window.addEventListener('mousedown', this.boundMouseDown);
    window.addEventListener('mouseup', this.boundMouseUp);
    window.addEventListener('mousemove', this.boundMouseMove);
  }

  /**
   * Update the WebSocket reference without re-registering keyboard listeners.
   * Called on reconnection when NetworkManager creates a new WebSocket.
   */
  updateWebSocket(ws: WebSocket): void {
    this.ws = ws;
  }

  stop(): void {
    if (this.contextMenuPendingTimer !== null) {
      clearTimeout(this.contextMenuPendingTimer);
      this.contextMenuPendingTimer = null;
    }
    this.ws = null;
    window.removeEventListener('keydown', this.boundKeyDown);
    window.removeEventListener('keyup', this.boundKeyUp);
    window.removeEventListener('blur', this.boundBlur);
    document.removeEventListener('visibilitychange', this.boundVisibilityChange);
    document.removeEventListener('contextmenu', this.boundContextMenu, true);
    window.removeEventListener('mousedown', this.boundMouseDown);
    window.removeEventListener('mouseup', this.boundMouseUp);
    window.removeEventListener('mousemove', this.boundMouseMove);
    this.keys = {
      thrust: false,
      strafeLeft: false,
      strafeRight: false,
      brake: false,
      afterburner: false,
      fire: false,
    };
  }

  /**
   * Set the world-space projection function. Called once when camera is ready.
   * The function takes screen coordinates and returns world-space {x, y}.
   */
  setWorldProjection(fn: (sx: number, sy: number) => { x: number; y: number }): void {
    this.unprojectFn = fn;
  }

  /**
   * Update the ship's world position for targetAngle calculation.
   * Called each frame from the game loop before tick().
   */
  setShipWorldPosition(x: number, y: number): void {
    this.shipWorldX = x;
    this.shipWorldY = y;
  }

  /**
   * Suppress or resume input processing.
   * When suppressed, tick() returns null and all keys are cleared.
   * Sends a zero-input frame to the server to clear held input immediately.
   * Used when settings panel or other UI overlays are open.
   */
  setSuppressed(value: boolean): void {
    this.suppressed = value;
    if (value) {
      this.clearAllKeys();
      this.sendClearInput();
    }
  }

  /**
   * Send a single zero-input frame to clear the server's held input.
   * The server's hold-previous model keeps replaying the last input indefinitely,
   * so we must explicitly send bitfield=0 when suppression starts.
   */
  private sendClearInput(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;

    const sequence = useGameStore.getState().nextInputSequence();
    const frame: InputFrame = {
      sequence,
      tick: 0, // tick is informational; server uses sequence for ordering
      bitfield: 0,
      targetAngle: this.targetAngle,
    };

    const buf = encodeInputMessage(frame);
    this.ws.send(buf);
    useGameStore.getState().addPendingInput(frame);
  }

  /**
   * Get current input bitfield and targetAngle, send to server.
   * Called each client tick.
   */
  tick(clientTick: number): InputFrame | null {
    if (this.suppressed) return null;
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return null;

    // Compute targetAngle from mouse position
    if (this.mouseHasMoved && this.unprojectFn) {
      const worldPos = this.unprojectFn(this.mouseScreenX, this.mouseScreenY);
      const dx = worldPos.x - this.shipWorldX;
      const dy = worldPos.y - this.shipWorldY;
      if (dx !== 0 || dy !== 0) {
        this.targetAngle = Math.atan2(dy, dx);
      }
    }

    const bitfield = encodeInput(this.keys);
    const sequence = useGameStore.getState().nextInputSequence();

    const frame: InputFrame = {
      sequence,
      tick: clientTick,
      bitfield,
      targetAngle: this.targetAngle,
    };

    // Send to server
    const buf = encodeInputMessage(frame);
    this.ws.send(buf);

    // Store for prediction
    useGameStore.getState().addPendingInput(frame);

    return frame;
  }

  getCurrentBitfield(): number {
    return encodeInput(this.keys);
  }

  getTargetAngle(): number {
    return this.targetAngle;
  }

  private onMouseMove(e: MouseEvent): void {
    this.mouseScreenX = e.clientX;
    this.mouseScreenY = e.clientY;
    this.mouseHasMoved = true;
  }

  private onKeyDown(e: KeyboardEvent): void {
    this.updateKey(e.code, true);
  }

  private onKeyUp(e: KeyboardEvent): void {
    this.updateKey(e.code, false);
  }

  private onBlur(): void {
    this.clearAllKeys();
  }

  private onVisibilityChange(): void {
    if (document.hidden) {
      this.clearAllKeys();
    }
  }

  private onContextMenu(e: Event): void {
    e.preventDefault();
    this.contextMenuFired = true;
  }

  private onMouseDown(e: MouseEvent): void {
    if (e.button === 0) {
      this.keys = { ...this.keys, fire: true };
    }
  }

  private onMouseUp(e: MouseEvent): void {
    if (e.button === 0) {
      this.keys = { ...this.keys, fire: false };
      return;
    }
    if (e.button !== 2) return;
    if (this.contextMenuPendingTimer !== null) {
      clearTimeout(this.contextMenuPendingTimer);
    }
    this.contextMenuFired = false;
    this.contextMenuPendingTimer = setTimeout(() => {
      this.contextMenuPendingTimer = null;
      if (!this.contextMenuFired) {
        this.clearAllKeys();
      }
    }, CONTEXT_MENU_DETECT_DELAY_MS);
  }

  private clearAllKeys(): void {
    this.keys = {
      thrust: false,
      strafeLeft: false,
      strafeRight: false,
      brake: false,
      afterburner: false,
      fire: false,
    };
  }

  private updateKey(code: string, pressed: boolean): void {
    switch (code) {
      case 'KeyW':
      case 'ArrowUp':
        this.keys = { ...this.keys, thrust: pressed };
        break;
      case 'KeyA':
      case 'ArrowLeft':
        this.keys = { ...this.keys, strafeLeft: pressed };
        break;
      case 'KeyD':
      case 'ArrowRight':
        this.keys = { ...this.keys, strafeRight: pressed };
        break;
      case 'KeyS':
      case 'ArrowDown':
        this.keys = { ...this.keys, brake: pressed };
        break;
      case 'ControlLeft':
      case 'ControlRight':
      case 'ShiftLeft':
      case 'ShiftRight':
        this.keys = { ...this.keys, afterburner: pressed };
        break;
    }
  }
}
